import { autoinject, transient, observable, computedFrom, BindingEngine } from "aurelia-framework";
import { DataType, QueryFilter, DataGridColumn, SourceType } from "../../../../models/queries/queries.models";
import { I18N } from "aurelia-i18n";
import * as moment from "moment";
import { LookupClientService } from "../../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue, LookupValueResponse } from "../../../../models/lookups/lookup.models";
import { LookupValueQuery } from "../../../../constants/modules/lookup.constants";
import { MobileHelper } from "../../../../helpers/mobile-helper";
import { EntityStatusChangeModel, SignalFunctions } from "../../../../services/basics/signal-functions";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import * as Enumerable from "linq";
import { SafetyRatingReviewStatusEnum } from "../../../../models/vetting/vetting.models";
import { Router } from "aurelia-router";
import { BaseActivity } from "./base-activity";
import { TasksConstants } from "../../../../models/tasks/task.model";
import KeyDownEvent = JQuery.KeyDownEvent;
import { Guid } from "../../../../utilities/guid";
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { UserClaimsService } from "../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../constants/authentication/claims";
import { QueriesClientService } from "../../../../services/queries/queries-client.services";
import { RatingItem } from "../../../components/rating-selector/rating-selector";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Reflection } from "../../../../utilities/reflection";
import { cachable } from '@rightship-tech/common-ui/decorators/cachable';

@autoinject()
@transient()
export class SafetyRatingReviewActivities extends BaseActivity {

    @cachable
    public selectedStatusFilter: number[] = [];
    @cachable
    public mobileSelectedFilter: number[] = [];
    public safetyRatingReviewStatuses: LookupValue[];
    public safetyRatingReviewOutcomes: LookupValue[];
    public isMobileFilterOpen: boolean;

    @observable()
    @cachable
    filter: string;

    @cachable
    public appliedFilter: string;
    public defaultPageCount: number = 100;
    @cachable
    public mySafetyRatingReviewsFilterSelected: boolean = false;

    public getItemRoute = (id) => `/review/${id}?source=activities`;
    public newSafetyRatingReviewCount: number;
    public inProgressCount: number;
    public awaitingResponseCount: number;
    public reviewRequiredCount: number;
    public mySafetyRatingReviewCount: number;
    public rightShipUser: boolean;
    public initialFilters: QueryFilter[];
    @cachable
    public rightshipMember: boolean = false;
    public isColumnFiltering: boolean = false;


    constructor(i18N: I18N, protected bindingEngine: BindingEngine, private lookupClientService: LookupClientService, private signalClientService: SignalClientService, router: Router,
        private userClientService: UserClientService, private userClaimsService: UserClaimsService, private queryService: QueriesClientService) {
        super(i18N, router, TasksConstants.SafetyRatingReviewListName, bindingEngine);
    }

    async init() {

        this.rightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        const request = new LookupValueRequest();
        request.lookupValueQuery = LookupValueQuery.SafetyRatingReviewStatuses;

        const result = await this.lookupClientService.getLookup(request);

        const handleStatusLookupResult = (x: LookupValueResponse) => {
            const values = x.activeValues;

            this.safetyRatingReviewStatuses = Enumerable.from(values).orderBy(x => x.additionalData.statusRank)
                .where(x =>
                    x.id != SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment
                    && x.id != SafetyRatingReviewStatusEnum.Processing
                    && x.id != SafetyRatingReviewStatusEnum.Accepted2
                    && x.id != SafetyRatingReviewStatusEnum.AcceptedFromSystem
                    && x.id != SafetyRatingReviewStatusEnum.Canceled
                    && x.id != SafetyRatingReviewStatusEnum.Rejected).toArray();
            this.safetyRatingReviewOutcomes = Enumerable.from(values).orderBy(x => x.id)
                .where(x =>
                    x.id == SafetyRatingReviewStatusEnum.Accepted2
                    || x.id == SafetyRatingReviewStatusEnum.AcceptedFromSystem
                    || x.id == SafetyRatingReviewStatusEnum.Canceled
                    || x.id == SafetyRatingReviewStatusEnum.Rejected).toArray();
        };

        handleStatusLookupResult(result);
        this.columns = [];
        this.columns.push(
            <DataGridColumn>
            {
                dataType: DataType.CustomTemplate,
                value: '<div class="column">' +
                    '<div class="row align-items-middle">' +
                    '<a class="link" href.bind="getItemRoute(item.safetyRatingReviewId)" mousedown.trigger="itemClick()">${item.vesselName}</a>' +
                    '<svg class="rs-badge" viewBox="0 0 30 30" if.bind="item.isRightShipMember && isRightShipUser">' +
                    '<g>' +
                    '<path fill="#003745" d="M2,0h26c1.1,0,2,0.9,2,2v26c0,1.1-0.9,2-2,2H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '<path fill="#FF5F1B" d="M2,0h1.5c0.5,0,1,0.2,1.4,0.6l15.5,14.8l0,0L4.9,29.5C4.5,29.8,4,30,3.5,30H2c-1.1,0-2-0.9-2-2V2C0,0.9,0.9,0,2,0z"/>' +
                    '</g>' +
                    '</svg>' +
                    '</div>' +
                    '<span if.bind="item.internationalMaritimeOrganizationNumber">IMO ${item.internationalMaritimeOrganizationNumber}</span></div>' +
                    '<span if.bind="item.vehicleIdentificationNumber">VIN ${item.vehicleIdentificationNumber}</span></div>' +
                    '<span if.bind="item.europeanNumberOfIdentification">ENI ${item.europeanNumberOfIdentification}</span></div>' +
                    '</div>',
                name: "vesselName",
                title: "Name",
                instancePerRow: true,
                sortOrder: 1,
                columnOrder: -1,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    getItemRoute: this.getItemRoute.bind(this),
                    itemClick: this.itemClick.bind(this),
                    isRightShipUser: this.rightShipUser
                }
            },
            <DataGridColumn>{
                columnOrder: 2,
                sortOrder: 10,
                dataType: DataType.DateTime,
                name: 'submittedDate',
                title: 'Date submitted',
                isSortable: true,
                width: "140px",
                valueConverter: (date: Date | string) => date
                    ? moment(date).format('DD MMM YYYY HH:mm')
                    : '',
            },
            <DataGridColumn>{
                dataType: DataType.CustomTemplate,
                value: '<comp-safety-score score.bind="item.safetyRating" inspection.bind="item"" safety-rating-indicative.bind="item.safetyRatingIndicative" safety-score-type.bind="item.safetyRatingType"' +
                    'has-error.bind="item.hasError" is-condensed="true" incident-grace-periods.bind="getIncidentGracePeriods(item)"></comp-safety-score>',
                name: "safetyRating",
                title: "Safety Score to review",
                instancePerRow: true,
                sortOrder: 3,
                columnOrder: 3,
                isHidden: false,
                isSortable: true,
                parameter: {
                    item: null,
                    getIncidentGracePeriods: (item) => {
                        return item.incidentGracePeriodJsonData ? JSON.parse(item.incidentGracePeriodJsonData) : [];
                    }
                },
                width: MobileHelper.isMobileDevice() && window.innerWidth < 1024 ? "" : "255px"
            },
            <DataGridColumn>{
                columnOrder: 4,
                sortOrder: 4,
                dataType: DataType.Number,
                instancePerRow: true,
                name: 'rulesFailed',
                title: 'Rules invoked',
                isSortable: true
            },
            <DataGridColumn>{
                columnOrder: 5,
                sortOrder: 5,
                dataType: DataType.Number,
                instancePerRow: true,
                name: 'unAnswered',
                title: 'Pending RFIs',
                isSortable: true
            },
            <DataGridColumn>{
                columnOrder: 6,
                sortOrder: 6,
                dataType: DataType.Number,
                instancePerRow: true,
                name: 'answered',
                title: 'Responded RFIs',
                isSortable: true
            },
            <DataGridColumn>{
                columnOrder: 7,
                sortOrder: 7,
                dataType: DataType.Date,
                instancePerRow: true,
                name: 'lastRequest',
                title: 'Last Request',
                valueConverter: (date: Date | string) => this.getDateToString(date, false),
                isSortable: true
            },
            <DataGridColumn>{
                columnOrder: 8,
                sortOrder: 8,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'requesterCustomer',
                title: 'Last Requested By',
                value:
                    '<div class="reviewer" >' +
                    '<span if.bind="item.requester" class="name">${item.requesterCustomer} - ${item.requester}' +
                    '</span>' +
                    '<span class="type" if.bind="item.safetyRatingReviewId">Review #${item.safetyRatingReviewId}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 9,
                sortOrder: 9,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'reviewer',
                title: 'Reviewer',
                value:
                    '<div class="reviewer" if.bind="item.reviewer">' +
                    '<span class="name">${item.reviewer}</span>' +
                    '<span class="type" if.bind="item.reviewerType">${item.reviewerType}</span>' +
                    '</div>',
                isSortable: true,
                parameter: {
                    item: null,
                }
            },
            <DataGridColumn>{
                columnOrder: 10,
                sortOrder: 1,
                dataType: DataType.CustomTemplate,
                instancePerRow: true,
                name: 'status',
                title: 'Status',
                value:
                    '<div class="status-badge-value" if.bind="item.status">' +
                    '<div class="row nowrap align-items-middle">' +
                    '<div class="status-badge-dot"></div>' +
                    '<span>${item.status}</span>' +
                    '<div class="info" if.bind="item.awaitingResponseStatusReason && item.status.toLowerCase() != \'unacceptable\' && item.status.toLowerCase() != \'acceptable\'">' +
                    '<i class="icon-info material-icons">info_outline</i>' +

                    //Tooltip
                    '<div class="status-tooltip">' +
                    '<div class="wrap-tooltip">' +
                    '<p class="desc">${item.awaitingResponseStatusReason}</p>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '</div>' +
                    '<span class="status-date">${getDateToString(item.statusDate)}</span>' +
                    '</div>',
                isSortable: true,
                width: "180px",
                getCellClass: (item: { status: string }) => {
                    return `status-badge ${this.getStatus(item.status)}`;
                },
                parameter: {
                    item: null,
                    getDateToString: (date: Date | string) => this.getDateToString(date)
                }
            });

        this.initialSorting = this.getSorting();
    }

    async activate(params: any) {
        this.params = params || {};
        await this.init();
        this.clearAllFilters(false);
        if (this.applyCache()) {
            this.setItemSource();
        } else {
            this.setDefaultFilters();
        }

        this.onActivated();
    }

    getStatus(status: string) {
        status = status.split(' ').join('-').toLowerCase();

        if (status.indexOf('(') != -1) {
            status = status.substr(0, status.indexOf('(') - 1);
        }

        return status;
    }

    public getSorting() {
        const columns: DataGridColumn[] = [];
        columns.push(
            <DataGridColumn>{
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 3 : 2,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: this.rightShipUser ? null : false,
                isHidden: true,
                name: 'submittedDate',
                title: 'SubmittedDate'
            },
            {
                columnOrder: -1,
                sortOrder: 1,
                dataType: DataType.Number,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusRank',
                title: 'StatusRank'
            },
            {
                columnOrder: -1,
                sortOrder: this.rightShipUser ? 2 : 3,
                dataType: DataType.DateTime,
                isSortable: true,
                isAscending: true,
                isHidden: true,
                name: 'statusDate',
                title: 'StatusDate'
            });
        return columns;
    }

    protected setDefaultFilters() {
        this.clearAllFilters(false);
        if (this.rightShipUser == true) {
            this.mobileSelectedFilter.push(SafetyRatingReviewStatusEnum.New);
            this.mobileSelectedFilter.push(SafetyRatingReviewStatusEnum.PendingReview);
        }

        this.mobileFilterApply(false);
    }

    private getItemSource(filters) {
        this.initialFilters = filters;
        return {
            sourceType: SourceType.ODataUrl,
            source: "odata/SafetyRatingReviewActivities",
            columns: this.columns,
            initialSorting: this.initialSorting,
            initialFilters: filters
        };
    }

    refreshActivityCounts() {
        if (this.userClaimsService.hasPermission(Claims.CanAccessSafetyRatingReviews)) {
            this.queryService.getData(`odata/SafetyRatingReviewActivities/Genesis.GetStatusCounts()`).then(x => {

                this.clearCounts();

                for (let i = 0; i < x.keys.length; i++) {

                    const key = x.keys[i];
                    const value = x.values[i];

                    if (key == SafetyRatingReviewStatusEnum.New) {
                        this.newSafetyRatingReviewCount = value;
                    } else if (key == SafetyRatingReviewStatusEnum.InProgress) {
                        this.inProgressCount = value;
                    } else if (key == SafetyRatingReviewStatusEnum.AwaitingResponse) {
                        this.awaitingResponseCount = value;
                    } else if (key == SafetyRatingReviewStatusEnum.PendingReview) {
                        this.reviewRequiredCount = value;
                    } else if (key == -1) {
                        this.mySafetyRatingReviewCount = value;
                    }
                }
            });
        }
    }

    clearCounts() {
        this.newSafetyRatingReviewCount = 0;
        this.inProgressCount = 0;
        this.awaitingResponseCount = 0;
        this.reviewRequiredCount = 0;
        this.mySafetyRatingReviewCount = 0;
    }

    getCount(statusId: number) {
        if (statusId == SafetyRatingReviewStatusEnum.New && this.newSafetyRatingReviewCount)
            return this.newSafetyRatingReviewCount;
        if (statusId == SafetyRatingReviewStatusEnum.InProgress && this.inProgressCount)
            return this.inProgressCount;
        if (statusId == SafetyRatingReviewStatusEnum.AwaitingResponse && this.awaitingResponseCount)
            return this.awaitingResponseCount;
        if (statusId == SafetyRatingReviewStatusEnum.PendingReview && this.reviewRequiredCount)
            return this.reviewRequiredCount;

        return 0;
    }

    rightshipMemberClick() {
        this.rightshipMember = !this.rightshipMember;
        this.setItemSource();
    }

    filterClick(status: LookupValue) {
        const index = this.selectedStatusFilter.indexOf(status.id);
        const isSelected = index != -1;
        if (!isSelected) {
            this.selectedStatusFilter.push(status.id);
        } else {
            this.selectedStatusFilter.splice(index, 1);
        }

        this.setItemSource();
    }

    mySafetyRatingReviewsClick() {
        this.mySafetyRatingReviewsFilterSelected = !this.mySafetyRatingReviewsFilterSelected;
        this.setItemSource();
    }

    private setItemSource() {

        const currentFilters: QueryFilter[] = [];

        if (this.selectedStatusFilter && this.selectedStatusFilter.length > 0) {
            this.selectedStatusFilter.forEach(x => {
                currentFilters.push(QueryFilter.getQuery("statusId", x, "eq", DataType.Number, false, false, "statusGroup", true));
            });
        }

        if (this.statusFilterIds && this.statusFilterIds.length > 0) {
            this.statusFilterIds.forEach(x => {
                currentFilters.push(QueryFilter.getQuery("statusId", x, "eq", DataType.Number, false, false, "statusGroup", true));
            });
        }

        if (this.mySafetyRatingReviewsFilterSelected) {
            if (this.rightShipUser)
                currentFilters.push(QueryFilter.getQuery("reviewerId", this.userClientService.currentUser.id, "eq", DataType.Number, true));
            else currentFilters.push(QueryFilter.getQuery("createdById", this.userClientService.currentUser.id, "eq", DataType.Number, true));
            currentFilters.push(QueryFilter.getQuery("statusId", [
                SafetyRatingReviewStatusEnum.Processing,
                SafetyRatingReviewStatusEnum.New,
                SafetyRatingReviewStatusEnum.InProgress,
                SafetyRatingReviewStatusEnum.AwaitingResponse,
                SafetyRatingReviewStatusEnum.PendingReview,
                SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment,
            ], "{0} in ({1})", DataType.Number, true, false, null, false, true));
        }

        //#region safety rating filter
        if (this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0) {
            const guid = Guid.generateGuid();
            const hasSafetyRating: boolean = this.selectedSafetyRatings.filter(x => x >= 0).length > 0;
            if (hasSafetyRating) {
                const safetyRatingFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRating",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: this.selectedSafetyRatings.filter(x => x >= 0),
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                currentFilters.push(safetyRatingFilter);
            }

            const isIncludeNa: boolean = this.selectedSafetyRatings.indexOf(-1) >= 0;
            if (isIncludeNa) {
                const naFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1})",
                        friendlyName: "in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRatingType",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: [-1],
                    isAnd: hasSafetyRating ? false : true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                currentFilters.push(naFilter);
            }
            else if (hasSafetyRating) {
                const naFilter: QueryFilter = {
                    filterOperator: {
                        translatedSymbol: "{0} in ({1}) eq false",
                        friendlyName: "not in",
                        requiresFilterValue: true,
                        requiresInterpolation: true
                    },
                    filterProperty: {
                        name: "safetyRatingType",
                        isDynamicProperty: false,
                        type: DataType.Number,
                        isNullable: true,
                    },
                    filterValue: [-1],
                    isAnd: true,
                    skipOnNullOrEmpty: true,
                    groupGuid: guid
                };
                currentFilters.push(naFilter);
            }
        }
        //#endregion

        if (this.appliedFilter) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("vesselName", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, true, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("internationalMaritimeOrganizationNumber", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
            currentFilters.push(QueryFilter.getQuery("safetyRatingReviewIdStr", this.appliedFilter, "contains({0}, '{1}')", DataType.Default, false, false, guid, true, true));
        }

        if (this.rightshipMember) {
            const guid = Guid.generateGuid();
            currentFilters.push(QueryFilter.getQuery("isRightShipMember", true, "eq", DataType.Boolean, true, false, guid, true));
        }

        this.itemSource = this.getItemSource(currentFilters);
        this.refreshActivityCounts();
    }

    filterKeyDown($event) {
        const e: KeyDownEvent = $event.detail;

        if (e.key == "Enter") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    filterChanged() {
        if ((this.filter && this.filter.length >= 5) || this.filter == "") {
            this.appliedFilter = this.filter;
            this.setItemSource();
        }
    }

    clearFilter() {
        this.filter = "";
        this.appliedFilter = this.filter;
        this.setItemSource();
    }

    attached() {
        this.signalClientService.subscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.SafetyRatingReview), this.refreshSafetyRatingReviewStatus);
    }

    detached() {
        this.signalClientService.unsubscribeToGroup(SignalFunctions.entityStatusChanged, SignalFunctions.entityTypeGroup(EntityTypes.SafetyRatingReview), this.refreshSafetyRatingReviewStatus);
        super.detached();
    }

    refreshSafetyRatingReviewStatus = (param: EntityStatusChangeModel) => {
        if (this.dataGridVM.page == 0) {
            this.dataGridVM.reloadData();
        } else {
            const item = this.dataGridVM.itemsCollection.filter(x => x.item.safetyRatingReviewId == param.entityId)[0];
            if (item) {
                item.item.statusId = param.statusId;
                item.item.status = this.safetyRatingReviewStatuses.filter(x => x.id == param.statusId).map(x => x.name)[0];
            }
        }
        this.refreshActivityCounts();
    }

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showSafetyRatingReviewOutcomeFilter),
        Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showStatusFilter),
        Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showSafetyRatingFilter),
        Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showColumnSorting))
    public get isSubFilterOpen(): boolean {
        return this.showSafetyRatingReviewOutcomeFilter || this.showStatusFilter || this.showSafetyRatingFilter || this.showColumnSorting;
    }

    toggleMobileFilterDialog() {
        if (!this.isSubFilterOpen) {
            this.isMobileFilterOpen = !this.isMobileFilterOpen;
        }

        this.showSafetyRatingReviewOutcomeFilter = false;
        this.showStatusFilter = false;
        this.showColumnSorting = false;
        this.showSafetyRatingFilter = false;
    }

    public mobileFilterClick(value: LookupValue) {
        const index = this.mobileSelectedFilter.indexOf(value.id);
        const isSelected = index != -1;

        if (!isSelected) {
            this.mobileSelectedFilter.push(value.id);
        } else this.mobileSelectedFilter.splice(index, 1);

        this.setItemSource();
    }

    public mobileFilterApply(toggleDialog: boolean = true) {
        if (toggleDialog)
            this.toggleMobileFilterDialog();

        this.appliedFilter = this.filter;
        this.selectedStatusFilter = [];
        const column = this.columns.filter(x => x.name == 'status')[0];
        column.filters = [];

        if (this.mobileSelectedFilter && this.mobileSelectedFilter.length > 0) {
            for (const item of this.mobileSelectedFilter)
                this.filterClick({ id: item, name: '' });
        } else {
            this.setItemSource();
        }
    }

    clearAllFilters(reload: boolean = true) {
        if (this.dataGridVM) {
            this.dataGridVM.clearSorting();
            this.isColumnFiltering = false
        }
        this.appliedFilter = null;
        this.selectedSafetyRatings = null;
        this.filter = null;
        this.mobileSelectedFilter = [];
        this.selectedStatusFilter = [];
        this.mySafetyRatingReviewsFilterSelected = false;
        this.statusFilterIds = [];
        this.selectedColumnSorting = [];
        this.rightshipMember = false;

        if (reload)
            this.setItemSource();
    }

    @computedFrom(Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showSafetyRatingReviewOutcomeFilter),
        Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showStatusFilter),
        Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showSafetyRatingFilter),
        Reflection.getPropertyPath<SafetyRatingReviewActivities>(z => z.showColumnSorting))
    public get mobileFilterSelected() {
        return this.showSafetyRatingFilter || this.showSafetyRatingReviewOutcomeFilter || this.showStatusFilter || this.showColumnSorting;
    }

    public closeAllFilters() {
        this.showSafetyRatingReviewOutcomeFilter = false;
        this.showSafetyRatingFilter = false;
        this.showStatusFilter = false;
        this.showColumnSorting = false;
        return false;
    }

    public selectedFilter: { title: string, clear: () => void, apply: () => void };


    public showSafetyRatingReviewOutcomeFilter: boolean = false;



    public mobileApplySelectedFilter() {
        if (this.selectedFilter) {
            this.selectedFilter.apply();
            this.selectedFilter = null;
        }
    }

    public showStatusFilter: boolean = false;
    @cachable
    public statusFilterIds: number[] = [];

    toggleStatusFilters() {
        if (!this.showStatusFilter) {
            this.closeAllFilters();
        }
        this.showStatusFilter = !this.showStatusFilter;
        this.selectedFilter = {
            title: "Status",
            clear: () => {
                this.statusFilterIds = [];
                this.setItemSource();
            },
            apply: () => {
                this.setItemSource();
                this.showStatusFilter = false;
            }
        }
    }

    onStatusFilter(event, reload: boolean = true) {
        this.statusFilterIds = event.filters.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.showStatusFilter = false;
    }

    @computedFrom('statusFilterIds', 'statusFilterIds.length')
    public get statusFilterCount() {
        return this.statusFilterIds ? this.statusFilterIds.length : 0;
    }

    public getStatusFilterIds(): number[] {
        return [
            SafetyRatingReviewStatusEnum.New,
            SafetyRatingReviewStatusEnum.PendingReview,
            SafetyRatingReviewStatusEnum.SafetyRatingReviewReassignment
        ];
    }

    @computedFrom("safetyRatingReviewStatuses", "safetyRatingReviewStatuses.length")
    public get safetyRatingReviewStatusFilters() {
        if (!this.safetyRatingReviewStatuses || this.safetyRatingReviewStatuses.length == 0) return [];
        return this.safetyRatingReviewStatuses.filter(x => this.getStatusFilterIds().indexOf(x.id) > -1);
    }

    @computedFrom("statusFilterIds", "statusFilterIds.length")
    public get statusFilterActive() {
        return this.statusFilterIds && this.statusFilterIds.length > 0;
    }

    //safety Score filter
    public showSafetyRatingFilter: boolean = false;
    @cachable
    public selectedSafetyRatings: number[] = [];

    public toggleSafetyRatingFilters() {
        if (!this.showSafetyRatingFilter) {
            this.closeAllFilters();
        }
        this.showSafetyRatingFilter = !this.showSafetyRatingFilter;
        this.selectedFilter = {
            title: "Safety Score",
            clear: () => {
                this.clearSafetyRatingFilter();
            },
            apply: () => {
                this.applySafetyRatingFilter(this.selectedSafetyRatings.map(r => new RatingItem(true, r, r == -1 ? -1 : -3))); //if rating is -1 we set the rating type to Na (-1) and if its not -1 we set it to full rating (-3)
            }
        }
    }

    public applySafetyRatingFilter(ratings: RatingItem[]) {
        this.selectedSafetyRatings = [];

        if (ratings && ratings.length > 0) {
            this.selectedSafetyRatings = this.selectedSafetyRatings.concat(ratings.map(x => x.rating));
        }

        this.setItemSource();

        this.toggleSafetyRatingFilters();
    }

    public clearSafetyRatingFilter() {
        this.selectedSafetyRatings = [];
        this.setItemSource();
    }

    @computedFrom("selectedSafetyRatings", "selectedSafetyRatings.length")
    public get safetyRatingFilterActive() {
        return this.selectedSafetyRatings && this.selectedSafetyRatings.length > 0;
    }

    //#endregion

    //#region column sorting 

    public showColumnSorting: boolean = false;
    public selectedColumnSorting: number[] = [];

    public toggleColumnSortingFilters() {
        if (!this.showColumnSorting) {
            this.closeAllFilters();
        }
        this.showColumnSorting = !this.showColumnSorting;
        this.selectedFilter = {
            title: "Column sorting",
            clear: () => {
                this.clearAllFilters();
            },
            apply: () => {
                this.applyColumnSortingFilter(this.selectedColumnSorting.map(bId => {
                    return { id: bId };
                }), false);
            }
        }
    }

    public applyColumnSortingFilter(items: any[], reload: boolean = true) {
        this.selectedColumnSorting = items.map(x => x.id);
        if (reload == true) this.setItemSource();
        this.toggleColumnSortingFilters();
    }

    sortByMobile(column: DataGridColumn) {
        this.dataGridVM.sortingTriggered(column);
        this.isColumnFiltering = this.dataGridVM.visibleColumns.some(item => item.isAscending === true || item.isAscending === false);
        this.setItemSource();
    }

    sortClass(column: DataGridColumn) {
        if (column.isAscending) {
            return "Ascending"
        }
        else if (column.isAscending == false) {
            return "Descending"
        }
        else {
            return "";
        }
    }

    //#endregion
}